import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { Form } from "semantic-ui-react";
import { RoomTypeAddonInventoryMapping, RoomTypes } from "../../../api/services/next";
import EditEntityPopup from '../../../components/shared/EditEntityPopup';
import { onError, onSuccess } from "../../../components/shared/messages";
import useBoolean from "../../../hooks/useBoolean";
import { useTextResources } from '../../../language';
import Transfer from "../../../components/shared/Transfer";
const AssignRoomTypesToHotelAddonInventory = ({
    open,
    onClose,
    hotel,
    hotelAddonInventory
}) => {
    const text = useTextResources();
    // const message = useMessageResources();
    const isCreate = true;
    const {
        value: isLoading,
        setTrue: setLoading,
        setFalse: resetLoading
    } = useBoolean(false);

    const [groupState, setGroupState] = useState({});

    useEffect(() => {
        setHotelAddonInventoryId(hotelAddonInventory.Id)
    }, [hotelAddonInventory.Id])

    useEffect(() => {
        setHotelId(hotel.Id)
    }, [hotel.Id])

    const setRoomTypeIds = useCallback((value) => {
        setGroupState((prevState) => ({
            ...prevState,
            RoomTypeIds: value,
        }));
    }, []);

    const setHotelAddonInventoryId = useCallback((value) => {
        setGroupState((prevState) => ({
            ...prevState,
            HotelAddonInventoryId: value,
        }));
    }, []);

    const setHotelId = useCallback((value) => {
        setGroupState((prevState) => ({
            ...prevState,
            HotelId: value,
        }));
    }, []);

    const handleSave = useCallback(async () => {
        try {
            setLoading();
            await RoomTypeAddonInventoryMapping.CreateRoomTypeAddonInventoryMapping(groupState);
            onSuccess();
            return true;
        } catch (error) {
            onError(error)
            return false;
        } finally {
            resetLoading();
        }
    }, [groupState, resetLoading, setLoading]);

    const handleClose = useCallback(() => {
        if (onClose) onClose();
        setGroupState(undefined)
    }, [onClose])

    const handleSaveAndClose = useCallback(async () => {
        const isSaved = await handleSave();
        if (isSaved) {
            handleClose();
        }
    }, [handleSave, handleClose]);

    return (
        <EditEntityPopup
            open={open}
            isCreate={isCreate}
            header={`${hotel.Name} - ${hotelAddonInventory.ChainAddon.Name}`}
            onClose={handleClose}
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
            isLoading={isLoading}
        >
            <Form>
                <Form.Field
                    label={text("RoomTypes")}
                    control={RoomTypesGroupTransfer}
                    hotelId={hotel.Id}
                    hotelAddonInventoryId={hotelAddonInventory.Id}
                    onChange={setRoomTypeIds}
                />
            </Form>
        </EditEntityPopup>
    );
};

AssignRoomTypesToHotelAddonInventory.propTypes = {
    open: PropTypes.bool.isRequired,
    hotel: PropTypes.object.isRequired,
    hotelAddonInventory: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
};

AssignRoomTypesToHotelAddonInventory.defaultProps = {
    open: false,
    onClose: () => { },
}

export default AssignRoomTypesToHotelAddonInventory;


const RoomTypesGroupTransfer = ({
    hotelId,
    hotelAddonInventoryId,
    onChange,
}) => {
    const [roomTypes, setRoomTypes] = useState([]);
    const [targetKeys, setTargetKeys] = useState([]);

    const loadRoomTypes = useCallback(async (hotelId) => {
        const roomTypes = await RoomTypes.GetRoomTypes({
            HotelId: hotelId,
        });
        setRoomTypes(roomTypes);
    }, []);

    const loadHotelInventoryRoomTypes = useCallback(async (hotelAddonInventoryId) => {
        const data = await RoomTypeAddonInventoryMapping.GetRoomTypeAddonInventoryMapping({
            HotelAddonInventoryId: hotelAddonInventoryId
        })

        const roomTypeIds = data?.map(x => x.RoomTypeId);

        setTargetKeys(roomTypeIds)
    }, [])

    useEffect(() => {
        if (hotelId > 0) {
            loadRoomTypes(hotelId);
            loadHotelInventoryRoomTypes(hotelAddonInventoryId)
        } else {
            setRoomTypes([])
            setTargetKeys([])
        }
    }, [hotelId, hotelAddonInventoryId])

    useEffect(() => {
        onChange(targetKeys)
    }, [targetKeys, onChange])

    const searchSelector = useCallback((roomType, searchTerm) =>
        roomType.Name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        roomType.ShortName?.toLowerCase().includes(searchTerm?.toLowerCase()), []);

    const handleGroupChange = useCallback((keys) => {
        setTargetKeys(keys);
    }, []);

    const keySelector = useCallback((roomType) => roomType.Id, []);
    const itemRender = useCallback((roomType) => {
        const name = [];

        if (roomType.ShortName) {
            name.push(roomType.ShortName)
        }

        if (roomType.Name) {
            name.push(roomType.Name)
        }

        return name.join('-')
    }, []);

    return (
        <Transfer
            items={roomTypes}
            itemKeySelector={keySelector}
            itemRender={itemRender}
            targetKeys={targetKeys}
            hasSearch
            searchSelector={searchSelector}
            hasSelectAll
            onTargetKeysChange={handleGroupChange}
        />
    );
};

RoomTypesGroupTransfer.propTypes = {
    onChange: PropTypes.func.isRequired,
    hotelId: PropTypes.number.isRequired,
    hotelAddonInventoryId: PropTypes.number.isRequired,
};